import { Schema } from "effect";
import { DocVersion } from "./audit.ts";
import { DocumentId } from "./ids.ts";

// The dependency-graph view `read-dep-graph` returns and `parseDepGraph` produces (CON-449). Facts
// only — no severity, no rule names, no conflict verdicts; ordering/dependency judgement is the
// model's job. `headingPath` is present on every element as its actionable address — the same kind
// of address `search-document` returns; the caller `read-block`s it to get an editable
// `BlockLocator` when it needs to act. `NamedView` is deferred to a later unit (U6a).

export const NodeId = Schema.String.pipe(Schema.brand("NodeId"));
export type NodeId = typeof NodeId.Type;

export const NodeKind = Schema.Literal(
  "unit-plan",
  "grammar-topic",
  "can-do",
  "vocab",
  "preview-chunk",
);
export type NodeKind = typeof NodeKind.Type;

// No `prerequisite` — the live document has no prerequisite convention (that was Cervantes-
// inventory-derived, out of scope).
export const EdgeKind = Schema.Literal(
  "introduces",
  "preview",
  "staging-partner",
  "carries-forward",
  "composes",
);
export type EdgeKind = typeof EdgeKind.Type;

// A node's teaching position. `unitPlan`/`cycle`/`module` are the human-meaningful coordinates (the
// authored unit/cycle/module labels — never `unit`, glossary-compliant); `ordinal` is the
// parser-assigned index in per-document traversal order — the within-doc TOTAL order, letting the
// model spot inversions without a second lookup. Positions are NOT comparable across documents.
// Named `TeachingPosition` (not `Position`) — `presence.ts` already owns `Position` for viewer
// cursor placement, an unrelated concept.
export const TeachingPosition = Schema.Struct({
  unitPlan: Schema.String,
  cycle: Schema.optional(Schema.String),
  module: Schema.optional(Schema.String),
  ordinal: Schema.Int.pipe(Schema.nonNegative()),
});
export type TeachingPosition = typeof TeachingPosition.Type;

// ─── Node kind-specific attrs ───────────────────────────────────────────────────

// `bucket` is an OPEN normalized-string fact, not a closed enum: current docs say
// GRAMMAR-MODULE | PASSIVE (2-bucket model, PR #1465 / CON-384); retired bucket labels in
// legacy-vintage docs are preserved verbatim as facts.
export const GrammarTopicAttrs = Schema.Struct({
  bucket: Schema.String,
  sliceTaught: Schema.optional(Schema.String),
  deferred: Schema.optional(Schema.String),
  l1Contrast: Schema.optional(Schema.String),
});
export type GrammarTopicAttrs = typeof GrammarTopicAttrs.Type;

// A tally parsed from `####` heading labels, normalized to CONTEXT.md content_type spellings; an
// unrecognized label is tallied under its normalized raw label — never dropped.
export const UnitPlanAttrs = Schema.Struct({
  moduleContentTypes: Schema.Record({
    key: Schema.String,
    value: Schema.Int.pipe(Schema.nonNegative()),
  }),
});
export type UnitPlanAttrs = typeof UnitPlanAttrs.Type;

// `skillTag` is an OPEN normalized-string fact (trim + uppercase), not a closed enum — mirroring the
// `bucket` decision above. The seed carries SI/SR/SP *and* WP/WR; a closed `Literal("SI","SR","SP")`
// silently dropped every WP/WR can-do and would re-ship on skill-vocabulary evolution.
export const CanDoAttrs = Schema.Struct({
  babbelCode: Schema.String,
  skillTag: Schema.String,
});
export type CanDoAttrs = typeof CanDoAttrs.Type;

export const PreviewChunkAttrs = Schema.Struct({
  previewsTopicId: Schema.String,
  unpackedAt: Schema.optional(Schema.String),
});
export type PreviewChunkAttrs = typeof PreviewChunkAttrs.Type;

// One node PER THEME item (label = theme name) — never one node per lexeme.
export const VocabAttrs = Schema.Struct({
  lexemes: Schema.Array(Schema.String),
});
export type VocabAttrs = typeof VocabAttrs.Type;

// Fields every `DepNode` variant carries, regardless of kind.
const commonNodeFields = {
  label: Schema.String,
  position: TeachingPosition,
  headingPath: Schema.Array(Schema.String),
  documentId: DocumentId,
};

// A value in the id-keyed `DepGraph.nodes` table. A discriminated union on `kind` so a node's
// `attrs` can only ever be the shape its kind allows (illegal states unrepresentable) — e.g. a
// `vocab` node cannot carry a `babbelCode`.
export const DepNode = Schema.Union(
  Schema.Struct({ kind: Schema.Literal("unit-plan"), ...commonNodeFields, attrs: UnitPlanAttrs }),
  Schema.Struct({
    kind: Schema.Literal("grammar-topic"),
    ...commonNodeFields,
    attrs: GrammarTopicAttrs,
  }),
  Schema.Struct({ kind: Schema.Literal("can-do"), ...commonNodeFields, attrs: CanDoAttrs }),
  Schema.Struct({ kind: Schema.Literal("vocab"), ...commonNodeFields, attrs: VocabAttrs }),
  Schema.Struct({
    kind: Schema.Literal("preview-chunk"),
    ...commonNodeFields,
    attrs: PreviewChunkAttrs,
  }),
);
export type DepNode = typeof DepNode.Type;

// A compact named-field record (NOT a positional tuple). `to` is `NodeId | null` when the reference
// does not resolve inside the provided document(s) — paired with an `unresolvedRefs[]` entry.
// Illegal state unrepresentable: `resolved: true` with `to: null` is rejected.
export const DepEdge = Schema.Struct({
  from: NodeId,
  to: Schema.NullOr(NodeId),
  kind: EdgeKind,
  resolved: Schema.Boolean,
  // The raw reference string as authored (e.g. "U10 GRAMMAR-MODULE M1.2 [T27-G]", "→ A1.2").
  targetRef: Schema.String,
  // The authored surface form the edge is about, markup-stripped (e.g. "Soy, es" for a
  // carries-forward item) — a fact, NOT a form→node mapping.
  label: Schema.optional(Schema.String),
  fromHeadingPath: Schema.Array(Schema.String),
  documentId: DocumentId,
}).pipe(
  Schema.filter((edge) =>
    edge.resolved && edge.to === null
      ? "a DepEdge claiming resolved: true must not have to: null"
      : true,
  ),
);
export type DepEdge = typeof DepEdge.Type;

// The discriminator on why a reference did not resolve within the provided document(s) — an
// `unresolvedRefs[]` entry is the skill's cross-level fan-out seed, so lumping these together makes
// the skill chase non-existent targets: (a) `cross-level-deferral` — a resolvable-elsewhere
// reference (e.g. "→ A1.2", "→ A2") the skill should fan out on; (b) `terminal` — out-of-scope
// (e.g. "→ out of scope"), never a fan-out seed; (c) `open-range` — unclassifiable (e.g. "→ U7+"),
// not a seed.
export const UnresolvedRefKind = Schema.Literal("cross-level-deferral", "terminal", "open-range");
export type UnresolvedRefKind = typeof UnresolvedRefKind.Type;

// A small, actionable seed list, not a bulk projection — carries `headingPath` (its address) and,
// only from `joinDepGraphs`, an optional `candidates` list.
export const UnresolvedRef = Schema.Struct({
  documentId: DocumentId,
  from: NodeId,
  kind: UnresolvedRefKind,
  targetRef: Schema.String,
  headingPath: Schema.Array(Schema.String),
  // Populated ONLY by `joinDepGraphs` (U3, CON-449), and only for a normalized-surface-form match
  // that names MORE than one candidate topic across the provided docs — a homograph (e.g. reflexive
  // vs impersonal `se`) must never silently collapse to one `resolved: true` edge, so the candidate
  // set stays visible instead. `parseDepGraph` never sets this field.
  candidates: Schema.optional(Schema.Array(NodeId)),
});
export type UnresolvedRef = typeof UnresolvedRef.Type;

// ─── DerivedMetrics (Q1) — facts only, no severity/verdict fields ──────────────

// Count of `introduces` edges whose target is a `grammar-topic` node (kind-scoped, so the widened
// vocab/can-do `introduces` edges never inflate it).
export const UnitGrammarDensity = Schema.Struct({
  documentId: DocumentId,
  unitPlan: Schema.String,
  grammarTargetCount: Schema.Int.pipe(Schema.nonNegative()),
});
export type UnitGrammarDensity = typeof UnitGrammarDensity.Type;

// For each `preview` edge, whether an unpack position resolves — a previewed form with no unpack is
// a fact ("dangling preview"), not a verdict.
export const PreviewCoverage = Schema.Struct({
  documentId: DocumentId,
  previewChunk: NodeId,
  unpacked: Schema.Boolean,
  unpackedAt: Schema.optional(Schema.String),
});
export type PreviewCoverage = typeof PreviewCoverage.Type;

// Per unit-plan: how many `carries-forward` edges it authors (leans on earlier units) and how many
// name it as their target (later units lean on it).
export const CarriesForwardRollup = Schema.Struct({
  documentId: DocumentId,
  unitPlan: Schema.String,
  fanIn: Schema.Int.pipe(Schema.nonNegative()),
  fanOut: Schema.Int.pipe(Schema.nonNegative()),
});
export type CarriesForwardRollup = typeof CarriesForwardRollup.Type;

// The cumulative grammar-vs-culture&pragmatics series, deterministic from the `moduleContentTypes`
// tally on unit-plan attrs.
export const PacingPoint = Schema.Struct({
  documentId: DocumentId,
  unitPlan: Schema.String,
  cumulativeGrammarModule: Schema.Int.pipe(Schema.nonNegative()),
  cumulativeCulturePragmatics: Schema.Int.pipe(Schema.nonNegative()),
});
export type PacingPoint = typeof PacingPoint.Type;

// An edge whose `to` position is later than its `from` position (compared by the parser-assigned
// `ordinal`) — a fact ("points forward"), not a conflict. Within-document only: a resolved
// cross-document edge is definitionally a cross-level deferral, where "forward" is the expected
// healthy shape, so it is excluded here.
export const PointsForward = Schema.Struct({
  documentId: DocumentId,
  from: NodeId,
  to: NodeId,
  fromPosition: TeachingPosition,
  toPosition: TeachingPosition,
});
export type PointsForward = typeof PointsForward.Type;

export const DerivedMetrics = Schema.Struct({
  grammarDensity: Schema.Array(UnitGrammarDensity),
  previewCoverage: Schema.Array(PreviewCoverage),
  carriesForwardRollups: Schema.Array(CarriesForwardRollup),
  pacing: Schema.Array(PacingPoint),
  pointsForward: Schema.Array(PointsForward),
});
export type DerivedMetrics = typeof DerivedMetrics.Type;

// ─── The DepGraph envelope ──────────────────────────────────────────────────────

export const DepGraphSummary = Schema.Struct({
  nodesByKind: Schema.Record({ key: NodeKind, value: Schema.Int.pipe(Schema.nonNegative()) }),
  edgesByKind: Schema.Record({ key: EdgeKind, value: Schema.Int.pipe(Schema.nonNegative()) }),
  unresolvedCount: Schema.Int.pipe(Schema.nonNegative()),
  truncated: Schema.optional(Schema.Boolean),
});
export type DepGraphSummary = typeof DepGraphSummary.Type;

// The envelope every response — default and every named view — shares (a "list"/"ordered" view adds
// the `order` companion field, never a shape change to `nodes`). `nodes` is an id-keyed table
// (compact — no per-node id repetition); `edges` are compact named-field records.
export const DepGraph = Schema.Struct({
  documentId: DocumentId,
  version: DocVersion,
  // Echoes the named view when one was requested. `NamedView` itself is deferred to U6a; a bare
  // string placeholder until the view registry exists.
  view: Schema.optional(Schema.String),
  nodes: Schema.Record({ key: NodeId, value: DepNode }),
  edges: Schema.Array(DepEdge),
  order: Schema.optional(Schema.Array(NodeId)),
  unresolvedRefs: Schema.Array(UnresolvedRef),
  metrics: Schema.optional(DerivedMetrics),
  summary: DepGraphSummary,
});
export type DepGraph = typeof DepGraph.Type;
